import {
    Box,
    Divider,
    Drawer,
    Typography,
    useMediaQuery,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    MenuItem,
    Avatar,
    Tooltip
} from '@mui/material';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { BrowserView, MobileView } from 'react-device-detect';
import { useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Person, LocalHospital, Edit } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import LogoSection from '../LogoSection';
import CompanySection from '../companySection';
import { selectAccess } from '../../../store/userReducer';
import { useClinic } from '../../../utils/ClinicContext';
import { useRole } from '../../../utils/RoleContext';
import { APP_VERSION } from '../../../version';
import MenuList from './MenuList';
import { drawerWidth } from 'store/constant';

function getClinicName(selectedClinic) {
    return selectedClinic?.name || '';
}

function Sidebar({ drawerOpen, drawerToggle, window }) {
    const theme = useTheme();
    const matchUpMd = useMediaQuery(theme.breakpoints.up('md'));
    const navigate = useNavigate();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { role, setRole } = useRole();
    const { setClinic } = useClinic();

    const user = useSelector((state) => state.user);
    const roles = (user.roles || []).filter((r) => r.name !== 'BCK_ADMIN');
    const selectedRole = user.selectedRole;
    const selectedClinic = user.selectedClinic;

    const roleLabels = {
        PATIENT: t('profile.roles.PATIENT'),
        DOCTOR: t('profile.roles.DOCTOR'),
        CLINIC_ADMIN: t('profile.roles.CLINIC_ADMIN'),
        PATIENT_CARE_GIVER: t('profile.roles.PATIENT_CARE_GIVER'),
        CLINIC_CARE_GIVER: t('profile.roles.CLINIC_CARE_GIVER')
    };

    const [openSelectDialog, setOpenSelectDialog] = useState(false);

    const [selectedClinicUuid, setSelectedClinicUuid] = useState(
        selectedClinic?.uuid || null
    );

    const handleRoleClinicChange = ({ roleUuid }) => {
        const role = roles.find((r) => r.uuid === roleUuid);

        if (!role || role.name === 'BCK_ADMIN') return;

        const clinic = role.account;
        const roleName = role.name;

        dispatch(selectAccess({ clinic, roleUuid }));
        setClinic(clinic);
        sessionStorage.setItem('clinic', JSON.stringify(clinic));
        setRole(roleName);
        sessionStorage.setItem('role', roleName);

        navigate('/');
    };

    const roleName = roles.find((r) => r.uuid === selectedRole)?.name || '';
    const clinicName = getClinicName(selectedClinic);

    const drawer = (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Logo aligned like menu icons */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    height: 80,
                    px: 2,
                    py: 2
                }}
            >
                <LogoSection />
            </Box>

            <Divider sx={{ borderColor: 'white', width: '100%', mx: 'auto' }} />

            {/* Menu list */}
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
                <BrowserView>
                    <PerfectScrollbar component="div" style={{ height: '100%' }}>
                        <MenuList />
                    </PerfectScrollbar>
                </BrowserView>
                <MobileView>
                    <Box sx={{ px: 0 }}>
                        <MenuList />
                    </Box>
                </MobileView>
            </Box>

            {role != 'BCK_ADMIN' && (
                <>
                    {/* PERFIL/CLINICA section */}
                    <Box sx={{ px: 1, py: 1, textAlign: 'center' }}>
                        <Tooltip title="Alterar Clínica/Perfil" arrow>
                            <IconButton
                                size="large"
                                onClick={() => setOpenSelectDialog(true)}
                                sx={{
                                    bgcolor: 'rgba(255,255,255,0.1)',
                                    color: 'white',
                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
                                }}
                            >
                                <Edit fontSize="medium" />
                            </IconButton>
                        </Tooltip>

                        <List disablePadding sx={{ mt: 1 }}>
                            {/* PERFIL */}
                            <ListItem sx={{ px: 1, py: 0.5, alignItems: 'center' }}>
                                <ListItemAvatar sx={{ minWidth: 36 }}>
                                    <Avatar
                                        sx={{ bgcolor: 'transparent', color: 'white' }}
                                    >
                                        <Person fontSize="small" />
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                    primary={
                                        <Typography
                                            variant="body2"
                                            sx={{ color: 'white', fontWeight: 500 }}
                                        >
                                            {t('profile.labels.profile')}
                                        </Typography>
                                    }
                                    secondary={
                                        <Typography
                                            variant="caption"
                                            sx={{ color: 'white' }}
                                        >
                                            {roleLabels[roleName] || roleName}
                                        </Typography>
                                    }
                                />
                            </ListItem>

                            {/* CLÍNICA */}
                            <ListItem sx={{ px: 1, py: 0.5, alignItems: 'center' }}>
                                <ListItemAvatar sx={{ minWidth: 36 }}>
                                    <Avatar
                                        sx={{ bgcolor: 'transparent', color: 'white' }}
                                    >
                                        <LocalHospital fontSize="small" />
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                    primary={
                                        <Typography
                                            variant="body2"
                                            sx={{ color: 'white', fontWeight: 500 }}
                                        >
                                            {t('profile.labels.clinic')}
                                        </Typography>
                                    }
                                    secondary={
                                        <Typography
                                            variant="caption"
                                            sx={{ color: 'white' }}
                                        >
                                            {clinicName}
                                        </Typography>
                                    }
                                />
                            </ListItem>
                        </List>
                    </Box>

                    {/* Mudar Roles e Clinica */}
                    <Dialog
                        open={openSelectDialog}
                        onClose={() => setOpenSelectDialog(false)}
                        fullWidth
                    >
                        <DialogTitle
                            sx={{
                                fontSize: '1.3rem',
                                fontWeight: 'bold',
                                textAlign: 'center'
                            }}
                        >
                            {t('profile.dialog.selectClinicAndRole')}
                        </DialogTitle>
                        <DialogContent>
                            {/* Selecionar Clínica */}
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    mb: 1,
                                    fontWeight: 'bold',
                                    color: '#1976d2',
                                    fontSize: '1.2rem'
                                }}
                            >
                                {t('profile.labels.clinic')}
                            </Typography>
                            {Array.from(
                                new Map(
                                    roles.map((r) => [r.account.uuid, r.account])
                                ).values()
                            ).map(({ uuid, name }) => (
                                <MenuItem
                                    key={uuid}
                                    onClick={() => setSelectedClinicUuid(uuid)}
                                    selected={uuid === selectedClinicUuid}
                                    sx={{
                                        mb: 0.5,
                                        borderRadius: 1,
                                        bgcolor:
                                            uuid === selectedClinicUuid
                                                ? '#1976d2'
                                                : 'inherit',
                                        color:
                                            uuid === selectedClinicUuid
                                                ? 'white'
                                                : 'inherit',
                                        '&.Mui-selected': {
                                            bgcolor: '#1976d2 !important',
                                            color: 'white !important',
                                            '&:hover': {
                                                bgcolor: '#115293 !important'
                                            }
                                        }
                                    }}
                                >
                                    {name}
                                </MenuItem>
                            ))}

                            <Divider sx={{ my: 2 }} />

                            {/* Selecionar Role */}
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    mb: 1,
                                    fontWeight: 'bold',
                                    color: '#1976d2',
                                    fontSize: '1.2rem'
                                }}
                            >
                                {t('profile.labels.profile')}
                            </Typography>
                            {roles
                                .filter((r) => r.account.uuid === selectedClinicUuid)
                                .map(({ uuid, name }) => (
                                    <MenuItem
                                        key={uuid}
                                        onClick={() => {
                                            handleRoleClinicChange({
                                                roleUuid: uuid
                                            });
                                            setOpenSelectDialog(false);
                                        }}
                                        sx={{ mb: 0.5, borderRadius: 1 }}
                                    >
                                        {roleLabels[name] || name}
                                    </MenuItem>
                                ))}
                            {(!selectedClinicUuid ||
                                roles.filter((r) => r.account.uuid === selectedClinicUuid)
                                    .length === 0) && (
                                <Typography variant="caption" color="textSecondary">
                                    {t('profile.helper.selectClinicFirst')}
                                </Typography>
                            )}
                        </DialogContent>
                    </Dialog>
                </>
            )}
            {/* Footer */}
            <Box sx={{ mt: 'auto', textAlign: 'center', pb: 2 }}>
                {role != 'BCK_ADMIN' && (
                    <Divider
                        sx={{ borderColor: 'white', width: '60%', mx: 'auto', my: 1 }}
                    />
                )}
                <Typography variant="body2" sx={{ color: 'white', mb: 1 }}>
                    POWERED BY
                </Typography>
                <CompanySection />
                <Typography
                    variant="caption"
                    sx={{ color: 'white', opacity: 0.7, mb: 1 }}
                >
                    v{APP_VERSION}
                </Typography>
            </Box>
        </Box>
    );

    const container = window !== undefined ? () => window.document.body : undefined;

    return (
        <Box
            component="nav"
            sx={{ flexShrink: { md: 0 }, width: matchUpMd ? drawerWidth : 'auto' }}
            aria-label="mailbox folders"
        >
            <Drawer
                container={container}
                variant={matchUpMd ? 'persistent' : 'temporary'}
                anchor="left"
                open={drawerOpen}
                onClose={drawerToggle}
                sx={{
                    '& .MuiDrawer-paper': {
                        width: drawerWidth,
                        background: 'linear-gradient(180deg, #0A579B, #0091EA)',
                        color: theme.palette.text.secondary,
                        borderRight: 'none',
                        flexShrink: 0
                    }
                }}
                ModalProps={{ keepMounted: true }}
            >
                {drawer}
            </Drawer>
        </Box>
    );
}

Sidebar.propTypes = {
    drawerOpen: PropTypes.bool,
    drawerToggle: PropTypes.func,
    window: PropTypes.object
};

export default Sidebar;
